import React from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import CompanyStorefrontPage from './CompanyStorefrontPage';

/**
 * Phase 2.4 - Smart router for /r/:slug
 *
 * The QR labels encode `https://remobiles.com/r/{REF}` (REM-XXX format).
 * The same route was previously used by B2B company storefronts (slug-based).
 * We disambiguate based on the slug pattern:
 *   - /^REM\d+$/i  -> repair reference (auth-based redirect)
 *   - otherwise    -> render the legacy CompanyStorefrontPage as before
 *
 * For repair refs:
 *   - admin / super_admin / staff  -> /reparations/{ref}
 *   - anyone else (or anonymous)   -> /suivi/{ref}
 *
 * NOTE: we MUST wait for AuthContext.loading=false before deciding, otherwise
 * an admin with a token in localStorage but /auth/me not yet resolved would
 * be wrongly routed to the public /suivi/ page.
 */
const REPAIR_REF_RE = /^REM\d+$/i;
const STAFF_ROLES = new Set(['admin', 'super_admin', 'staff']);

const QrRouter = () => {
  const { slug } = useParams();
  const { user, isAuthenticated, loading } = useAuth();
  const { t } = useLanguage();

  if (!slug) {
    return <Navigate to="/" replace />;
  }

  // Repair QR
  if (REPAIR_REF_RE.test(slug)) {
    const ref = slug.toUpperCase();

    if (loading) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-950 via-slate-900 to-black text-amber-400" data-testid="qr-router-loading">
          <Loader2 className="w-6 h-6 animate-spin mr-2" />
          <span className="text-sm tracking-wider">{t('qrRouter.redirecting')}</span>
        </div>
      );
    }

    if (isAuthenticated && user && STAFF_ROLES.has(user.role)) {
      return <Navigate to={`/reparations/${ref}`} replace />;
    }
    return <Navigate to={`/suivi/${ref}`} replace />;
  }

  // Legacy: B2B company storefront
  return <CompanyStorefrontPage />;
};

export default QrRouter;
